import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import siteConfig from "@/config/site.json";
import { useT } from "@/lib/i18n";

export const Route = createFileRoute("/privacidad")({
  component: PrivacyPage,
  head: () => ({
    meta: [
      { title: `Privacy Policy — ${siteConfig.brand.name}` },
      {
        name: "description",
        content:
          "Privacy policy for Bosque Suspendido: how we process personal data, purposes, user rights and contact.",
      },
      { property: "og:title", content: `Privacy Policy — ${siteConfig.brand.name}` },
      {
        property: "og:description",
        content: "Privacy policy for Bosque Suspendido.",
      },
      { property: "og:url", content: "https://bosque-suspendido.lovable.app/privacidad" },
    ],
    links: [{ rel: "canonical", href: "https://bosque-suspendido.lovable.app/privacidad" }],
  }),
});

function PrivacyPage() {
  const { tk } = useT();
  const brand = siteConfig.brand.name;
  const email = siteConfig.contact.emails[0].address;

  return (
    <Layout>
      <article className="max-w-3xl mx-auto px-6 py-20">
        <p className="text-xs tracking-[0.3em] uppercase text-gold mb-4">
          {tk("legal.kicker", "Legal")}
        </p>
        <h1 className="text-4xl md:text-5xl font-serif mb-8">
          {tk("legal.privacy.title", "Privacy Policy")}
        </h1>
        <div className="gold-divider mb-10" />

        <div className="space-y-6 text-foreground/80 leading-relaxed">
          <p>
            {tk("legal.privacy.intro.a", "At ")}
            {brand}
            {tk(
              "legal.privacy.intro.b",
              " we process your personal data with the utmost care, respecting EU Regulation 2016/679 (GDPR) and applicable national data protection law.",
            )}
          </p>

          <h2 className="font-serif text-2xl text-foreground mt-10">
            {tk("legal.privacy.s1.t", "1. Data controller")}
          </h2>
          <p>
            {brand}
            {tk(
              "legal.privacy.s1.d",
              " is the controller of personal data provided through this website or through our published contact channels.",
            )}
          </p>

          <h2 className="font-serif text-2xl text-foreground mt-10">
            {tk("legal.privacy.s2.t", "2. Purposes")}
          </h2>
          <p>
            {tk(
              "legal.privacy.s2.d",
              "Your data will be processed to respond to enquiries, manage meetings and deliver the IT services you request, as well as to comply with legal obligations.",
            )}
          </p>

          <h2 className="font-serif text-2xl text-foreground mt-10">
            {tk("legal.privacy.s3.t", "3. Legal basis")}
          </h2>
          <p>
            {tk(
              "legal.privacy.s3.d",
              "The legal basis for processing is the data subject's consent, the performance of a contract or compliance with legal obligations.",
            )}
          </p>

          <h2 className="font-serif text-2xl text-foreground mt-10">
            {tk("legal.privacy.s4.t", "4. Retention")}
          </h2>
          <p>
            {tk(
              "legal.privacy.s4.d",
              "Data is kept for as long as needed to fulfil the purpose for which it was collected and to determine any resulting liabilities.",
            )}
          </p>

          <h2 className="font-serif text-2xl text-foreground mt-10">
            {tk("legal.privacy.s5.t", "5. Your rights")}
          </h2>
          <p>
            {tk(
              "legal.privacy.s5.a",
              "You can exercise your rights of access, rectification, erasure, opposition, restriction and portability by writing to ",
            )}
            <a className="text-gold hover:underline" href={`mailto:${email}`}>
              {email}
            </a>
            .
          </p>

          <h2 className="font-serif text-2xl text-foreground mt-10">
            {tk("legal.privacy.s6.t", "6. Processors and service providers")}
          </h2>
          <p>
            {brand}
            {tk(
              "legal.privacy.s6.d",
              " uses technology providers to host its website, send transactional communications and operate its support and booking systems. These providers act as processors and handle personal data only on our instructions and in accordance with applicable data protection law.",
            )}
          </p>
        </div>
      </article>
    </Layout>
  );
}
